import * as React from 'react';
import type { SectionSplit } from '@pageboss/shared';
import { Popover } from './popover';
import { cn } from '@/lib/utils';

interface SplitPopoverProps {
  open: boolean;
  anchorRect: DOMRect | null;
  onClose: () => void;
  onPick: (split: SectionSplit) => void;
  title?: string;
  // Which split options to show. Defaults to the 4 multi-column ones
  // (used by section toolbars converting an existing single section
  // into multi-column). Insert-section flow passes 'full' too.
  options?: readonly SectionSplit[];
}

interface SplitOption {
  split: SectionSplit;
  label: string;
  preview: React.ReactElement;
}

// 32×24 viewBox previews. Fill matches the template thumbnails used
// by the empty-page picker — warm gray, soft rounded.
function previewSvg(rects: Array<[number, number]>) {
  // rects is a list of [x, width] tuples. Height/y are fixed for
  // a horizontal row of column previews.
  return (
    <svg viewBox="0 0 32 24" aria-hidden className="h-6 w-8" role="img">
      {rects.map(([x, w], i) => (
        <rect key={i} x={x} y={2} width={w} height={20} rx={2.5} fill="var(--color-border-strong)" />
      ))}
    </svg>
  );
}

const SPLIT_OPTIONS: readonly SplitOption[] = [
  { split: 'full', label: 'Single column', preview: previewSvg([[1, 30]]) },
  { split: '2-1', label: '2-column · wide left', preview: previewSvg([[1, 19], [22, 9]]) },
  { split: '1-2', label: '2-column · wide right', preview: previewSvg([[1, 9], [12, 19]]) },
  { split: '1-1', label: '2-column · even', preview: previewSvg([[1, 14], [17, 14]]) },
  {
    split: '1-1-1',
    label: '3-column',
    preview: previewSvg([
      [1, 9],
      [12, 9],
      [23, 8],
    ]),
  },
];

const DEFAULT_OPTIONS: readonly SectionSplit[] = ['2-1', '1-2', '1-1', '1-1-1'];

export function SplitPopover({
  open,
  anchorRect,
  onClose,
  onPick,
  title = 'Split this row into',
  options = DEFAULT_OPTIONS,
}: SplitPopoverProps) {
  const items = SPLIT_OPTIONS.filter((opt) => options.includes(opt.split));

  return (
    <Popover open={open} anchorRect={anchorRect} onClose={onClose}>
      <div className="flex flex-col p-2">
        <div className="px-3 pb-1 pt-2 eyebrow">
          {title}
        </div>
        <ul className="space-y-px">
          {items.map((opt) => (
            <li key={opt.split}>
              <button
                type="button"
                onClick={() => onPick(opt.split)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors',
                  'hover:bg-surface-sunken focus-visible:bg-surface-sunken',
                )}
              >
                <span className="flex h-7 w-9 shrink-0 items-center justify-center rounded-sm bg-surface-sunken">
                  {opt.preview}
                </span>
                <span className="text-sm text-foreground">{opt.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Popover>
  );
}
